import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { CheckCircle, XCircle, Music2, ChevronLeft, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  pending:  "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  approved: "text-[#B026FF] bg-[#B026FF]/10 border-[#B026FF]/20",
  rejected: "text-red-400 bg-red-400/10 border-red-400/20",
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function AdminShows() {
  const [shows, setShows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadShows(); }, []);

  const loadShows = async () => {
    try {
      // No orderBy — legacy docs without createdAt are excluded by Firestore orderBy
      const snap = await getDocs(collection(db, "shows"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setShows(items);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "shows", id), { status });

      // Notify creator
      const show = shows.find((s) => s.id === id);
      if (show?.creatorId) {
        const message = status === "approved"
          ? `Your show "${show.title}" has been approved and is now live on your station!`
          : `Your show "${show.title}" has been rejected. Contact stagepassadmin@gmail.com for details.`;
        await addDoc(collection(db, "notifications", show.creatorId, "items"), {
          type: status === "approved" ? "show_approved" : "show_rejected",
          message,
          read: false,
          createdAt: serverTimestamp(),
        });
      }
    } catch (e) { console.error(e); }
    setShows((s) => s.map((x) => x.id === id ? { ...x, status } : x));
  };

  // "pending" tab also shows shows with no status field (legacy)
  const pendingCount = shows.filter((s) => !s.status || s.status === "pending").length;
  const filtered = shows.filter((s) => {
    if (filter === "all") return true;
    if (filter === "pending") return !s.status || s.status === "pending";
    return s.status === filter;
  });

  return (
    <div data-testid="admin-shows" className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1">
          <ChevronLeft size={16} /> Admin
        </Link>
        <h2 className="text-xl font-black text-white">Radio Shows</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
              filter === s ? "bg-[#B026FF]/20 text-[#B026FF] border border-[#B026FF]/30" : "bg-[#111] border border-white/10 text-zinc-400 hover:border-white/20"
            }`}>
            {s}
            {s === "pending" && pendingCount > 0 && (
              <span className="ml-1.5 bg-yellow-400/20 text-yellow-400 text-[10px] px-1.5 rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No shows with status: {filter}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((show) => (
            <div key={show.id} data-testid={`show-row-${show.id}`}
              className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl p-3">
              <div className="w-10 h-10 rounded-xl bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
                <Music2 size={16} className="text-zinc-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{show.title || "Untitled Show"}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border capitalize ${STATUS_COLOR[show.status] || STATUS_COLOR.pending}`}>
                    {show.status || "pending"}
                  </span>
                  {show.dayOfWeek !== undefined && (
                    <span className="text-zinc-600 text-xs flex items-center gap-1">
                      <Clock size={10} /> {DAYS[show.dayOfWeek]} {show.startTime}
                    </span>
                  )}
                  {show.creatorId && (
                    <span className="text-zinc-600 text-xs truncate">creator: {show.creatorId.slice(0, 8)}…</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {show.status !== "approved" && (
                  <button onClick={() => updateStatus(show.id, "approved")} data-testid={`approve-show-${show.id}`}
                    className="flex items-center gap-1 text-[10px] bg-[#B026FF]/10 border border-[#B026FF]/20 text-[#B026FF] px-2 py-1 rounded-lg hover:bg-[#B026FF]/20 transition-colors">
                    <CheckCircle size={11} /> Approve
                  </button>
                )}
                {show.status !== "rejected" && (
                  <button onClick={() => updateStatus(show.id, "rejected")} data-testid={`reject-show-${show.id}`}
                    className="flex items-center gap-1 text-[10px] bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-1 rounded-lg hover:bg-red-500/20 transition-colors">
                    <XCircle size={11} /> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
